// Generates an on-brand, modern PDF of a user's Capital Readiness report using
// pdfmake. Brand language mirrors the app: Montserrat typeface, the consulting
// blue (#1976D2) → navy (#17263A) palette, and the dashboard's own score-colour
// scale for the gauge / status. pdfmake, its font VFS and the embedded Montserrat
// fonts are all imported dynamically so they never ship in the main bundle.

import type { CapitalReport, ReportBlock, ReportSection, TableCell, Tone } from '@/data/capitalReports';
import { LOGO_WHITE_DATAURL } from '@/utils/brandLogo';

interface ReportPdfOptions {
  userName: string;
  score: number;
  dateLabel: string;
}

// ── Brand palette (from the app: index.css, authPages.css, Dashboard.tsx) ──────
const BRAND = '#1976D2';        // primary consulting blue
const BRAND_DEEP = '#125AA6';
const NAVY = '#17263A';         // brand ink
const BODY = '#42505F';
const MUTED = '#6E7888';
const FAINT = '#9AA4B2';
const LINE = '#E5E8EA';
const CARD = '#F6F9FD';
const BLUE_TINT = '#EAF2FB';

// Score / status colours — identical to the dashboard's Capital Readiness scale.
const STATUS: Record<Tone, string> = {
  red: '#C0392B', orange: '#E0592A', yellow: '#E08A1E', green: '#15824E', neutral: '#6E7888',
};
const STATUS_TINT: Record<Tone, string> = {
  red: '#FBEAE7', orange: '#FCEDE6', yellow: '#FBF1DF', green: '#E7F4EC', neutral: '#EEF2F7',
};

// Continuous score → colour, mirroring Dashboard.tsx crColor.
function scoreColor(s: number): string {
  if (s < 33) return '#C0392B';
  if (s < 41) return '#E0592A';
  if (s < 48) return '#E08A1E';
  if (s < 56) return '#E6B400';
  if (s < 65) return '#1976D2';
  if (s < 74) return '#0E8F6E';
  return '#15824E';
}
function riskLabel(s: number): string {
  if (s < 33) return 'Critical';
  if (s < 41) return 'Very High';
  if (s < 48) return 'High';
  if (s < 56) return 'Medium';
  if (s < 65) return 'Semi-Ready';
  if (s < 74) return 'Investor-Ready';
  return 'Exceptional';
}

const CONTENT_WIDTH = 515;
const PAGE_W = 595.28;
const PAGE_H = 841.89;

const toneColor = (t?: Tone) => STATUS[t ?? 'neutral'];

function escapeXml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

// ── SVG builders ───────────────────────────────────────────────────────────────

function coverBackgroundSvg(): string {
  return `<svg width="${PAGE_W}" height="${PAGE_H}" viewBox="0 0 ${PAGE_W} ${PAGE_H}" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <linearGradient id="bg" x1="0" y1="0" x2="0.4" y2="1">
      <stop offset="0" stop-color="#2E83E6"/>
      <stop offset="0.55" stop-color="${BRAND}"/>
      <stop offset="1" stop-color="${NAVY}"/>
    </linearGradient>
    <radialGradient id="glow" cx="0.85" cy="0.1" r="0.7">
      <stop offset="0" stop-color="#7FB4F0" stop-opacity="0.35"/>
      <stop offset="1" stop-color="#7FB4F0" stop-opacity="0"/>
    </radialGradient>
  </defs>
  <rect width="${PAGE_W}" height="${PAGE_H}" fill="url(#bg)"/>
  <rect width="${PAGE_W}" height="${PAGE_H}" fill="url(#glow)"/>
  <circle cx="535" cy="110" r="200" fill="none" stroke="#FFFFFF" stroke-opacity="0.07" stroke-width="1.5"/>
  <circle cx="535" cy="110" r="130" fill="none" stroke="#FFFFFF" stroke-opacity="0.06" stroke-width="1.5"/>
  <circle cx="40" cy="800" r="170" fill="none" stroke="#FFFFFF" stroke-opacity="0.05" stroke-width="1.5"/>
</svg>`;
}

function polar(cx: number, cy: number, r: number, deg: number) {
  const a = ((deg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
}
function arcPath(cx: number, cy: number, r: number, endDeg: number): string {
  const e = Math.min(359.999, Math.max(0.001, endDeg));
  const s = polar(cx, cy, r, e);
  const f = polar(cx, cy, r, 0);
  const large = e <= 180 ? '0' : '1';
  return `M ${s.x.toFixed(2)} ${s.y.toFixed(2)} A ${r} ${r} 0 ${large} 0 ${f.x.toFixed(2)} ${f.y.toFixed(2)}`;
}

// White "score card" centerpiece for the cover — legible on the blue gradient.
function scoreCardSvg(score: number): string {
  const W = 300, H = 300;
  const col = scoreColor(score);
  const cx = 150, cy = 132, r = 86, sw = 17;
  const pct = Math.max(0, Math.min(100, score)) / 100;
  const arc = arcPath(cx, cy, r, 360 * pct);
  const end = polar(cx, cy, r, 360 * pct);
  const risk = riskLabel(score).toUpperCase();
  const pillLabel = `RISK · ${risk}`;
  const pillW = Math.ceil(pillLabel.length * 6.6) + 46;
  const pillX = (W - pillW) / 2;
  return `<svg width="${W}" height="${H}" viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg">
  <rect x="8" y="12" width="${W - 16}" height="${H - 20}" rx="22" fill="#0B1B30" fill-opacity="0.18"/>
  <rect x="6" y="6" width="${W - 12}" height="${H - 20}" rx="22" fill="#FFFFFF"/>
  <circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="#EEF2F7" stroke-width="${sw}"/>
  <path d="${arc}" fill="none" stroke="${col}" stroke-width="${sw}" stroke-linecap="round"/>
  <circle cx="${end.x.toFixed(2)}" cy="${end.y.toFixed(2)}" r="${sw / 2 + 1}" fill="${col}"/>
  <text x="${cx}" y="${cy + 6}" text-anchor="middle" font-family="Montserrat" font-weight="bold" font-size="48" fill="${NAVY}">${score.toFixed(1)}</text>
  <text x="${cx}" y="${cy + 28}" text-anchor="middle" font-family="Montserrat" font-size="11" fill="${MUTED}">OUT OF 100</text>
  <rect x="${pillX}" y="244" width="${pillW}" height="30" rx="15" fill="${col}"/>
  <circle cx="${pillX + 16}" cy="259" r="4" fill="#FFFFFF"/>
  <text x="${pillX + 28}" y="263" font-family="Montserrat" font-weight="bold" font-size="11" fill="#FFFFFF">${escapeXml(pillLabel)}</text>
</svg>`;
}

function badgeSvg(n: number, color: string): string {
  return `<svg width="22" height="22" viewBox="0 0 22 22" xmlns="http://www.w3.org/2000/svg">
  <rect x="0" y="0" width="22" height="22" rx="7" fill="${color}"/>
  <text x="11" y="15.5" text-anchor="middle" font-family="Montserrat" font-weight="bold" font-size="12" fill="#FFFFFF">${n}</text>
</svg>`;
}

function stepperSvg(steps: string[], currentIndex: number | undefined, accent: string): { svg: string; width: number } {
  const w = 480, rowH = 42, x = 16, r = 7, padV = 10;
  const nodeY = (i: number) => padV + r + i * rowH;
  const h = nodeY(steps.length - 1) + r + padV;
  let p = `<svg width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" xmlns="http://www.w3.org/2000/svg">`;
  p += `<line x1="${x}" y1="${nodeY(0)}" x2="${x}" y2="${nodeY(steps.length - 1)}" stroke="${LINE}" stroke-width="2"/>`;
  if (currentIndex != null && currentIndex > 0) {
    p += `<line x1="${x}" y1="${nodeY(0)}" x2="${x}" y2="${nodeY(Math.min(currentIndex, steps.length - 1))}" stroke="${accent}" stroke-width="2"/>`;
  }
  steps.forEach((s, i) => {
    const y = nodeY(i);
    const done = currentIndex != null && i < currentIndex;
    const cur = i === currentIndex;
    const future = currentIndex != null && i > currentIndex;
    const fill = done || cur ? accent : '#FFFFFF';
    const stroke = future ? '#CBD5E1' : accent;
    if (cur) p += `<circle cx="${x}" cy="${y}" r="${r + 4}" fill="none" stroke="${accent}" stroke-opacity="0.25" stroke-width="2"/>`;
    p += `<circle cx="${x}" cy="${y}" r="${r}" fill="${fill}" stroke="${stroke}" stroke-width="2"/>`;
    if (done) p += `<path d="M ${x - 3.4} ${y + 0.2} l 2.3 2.5 l 4.4 -5" fill="none" stroke="#FFFFFF" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>`;
    const labelColor = future ? FAINT : NAVY;
    const weight = cur ? 'bold' : 'normal';
    const here = cur ? `<tspan fill="${accent}" font-weight="bold" font-size="8.5"> &#8226; YOU ARE HERE</tspan>` : '';
    p += `<text x="${x + 20}" y="${y + 4}" font-family="Montserrat" font-size="11" font-weight="${weight}" fill="${labelColor}">${escapeXml(s)}${here}</text>`;
  });
  p += `</svg>`;
  return { svg: p, width: w };
}

// Word-wrap a string into lines of at most `maxChars` characters (word boundaries).
function wrapText(text: string, maxChars: number): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let cur = '';
  for (const w of words) {
    const next = cur ? `${cur} ${w}` : w;
    if (next.length <= maxChars) cur = next;
    else { if (cur) lines.push(cur); cur = w; }
  }
  if (cur) lines.push(cur);
  return lines.length ? lines : [''];
}

// CTA banner with the headline rendered INSIDE the SVG (wrapped, vertically padded),
// so the banner height grows with the text and the bottom padding is always correct.
function ctaBannerSvg(text: string): { svg: string; height: number } {
  const w = CONTENT_WIDTH;
  const padTop = 17, padBot = 19, gap = 9, eyeH = 9, lineH = 19, fs = 13;
  const lines = wrapText(text, 52);
  const h = padTop + eyeH + gap + lines.length * lineH + padBot;
  const eyeY = padTop + eyeH;
  const firstLineY = eyeY + gap + 13;
  const titleSpans = lines
    .map((ln, i) => `<text x="${w / 2}" y="${firstLineY + i * lineH}" text-anchor="middle" font-family="Montserrat" font-weight="bold" font-size="${fs}" fill="#FFFFFF">${escapeXml(ln)}</text>`)
    .join('');
  return {
    height: h,
    svg: `<svg width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <linearGradient id="cta" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#2E83E6"/><stop offset="1" stop-color="${NAVY}"/></linearGradient>
    <clipPath id="ctaclip"><rect x="0" y="0" width="${w}" height="${h}" rx="14"/></clipPath>
  </defs>
  <g clip-path="url(#ctaclip)">
    <rect x="0" y="0" width="${w}" height="${h}" fill="url(#cta)"/>
    <circle cx="${w - 46}" cy="16" r="50" fill="#FFFFFF" fill-opacity="0.07"/>
    <circle cx="34" cy="${h - 10}" r="34" fill="#FFFFFF" fill-opacity="0.05"/>
  </g>
  <text x="${w / 2}" y="${eyeY}" text-anchor="middle" font-family="Montserrat" font-weight="bold" font-size="8" fill="#BFD9F5">YOUR NEXT MOVE</text>
  ${titleSpans}
</svg>`,
  };
}

// ── Block → pdfmake content ──────────────────────────────────────────────────

function tableWidths(cols: number): (number | string)[] {
  if (cols === 2) return [160, '*'];
  if (cols === 3) return [115, 150, '*'];
  return Array(cols).fill('*');
}

function statCard(label: string, value: TableCell) {
  const tone = value.tone;
  return {
    table: {
      widths: [4, '*'],
      body: [[
        { text: '', fillColor: tone ? toneColor(tone) : BRAND },
        {
          fillColor: CARD,
          margin: [13, 11, 12, 11],
          stack: [
            { text: label.toUpperCase(), font: 'MontserratSemi', color: MUTED, fontSize: 7.5, characterSpacing: 0.6, margin: [0, 0, 0, 4] },
            { text: value.text, bold: true, color: tone ? toneColor(tone) : NAVY, fontSize: 12.5 },
          ],
        },
      ]],
    },
    layout: 'noBorders',
  };
}

function statGrid(rows: TableCell[][]): any[] {
  const cards = rows.map((r) => statCard(r[0]?.text ?? '', r[1] ?? { text: '' }));
  const out: any[] = [];
  for (let i = 0; i < cards.length; i += 2) {
    out.push({ columns: [cards[i], cards[i + 1] ?? { text: '' }], columnGap: 10, margin: [0, 0, 0, 10] });
  }
  return out;
}

function renderTable(block: Extract<ReportBlock, { kind: 'table' }>): any {
  const header = block.headers.map((h) => ({
    text: h.toUpperCase(), font: 'MontserratSemi', color: '#FFFFFF', fillColor: NAVY,
    margin: [8, 7, 8, 7], fontSize: 8.5, characterSpacing: 0.4,
  }));
  const body = block.rows.map((row) =>
    row.map((cell) => {
      if (cell.tone) {
        return {
          // The status dot uses Roboto — Montserrat has no U+25CF glyph (renders as tofu).
          text: [{ text: '●  ', color: toneColor(cell.tone), font: 'Roboto' }, { text: cell.text, color: toneColor(cell.tone), bold: true }],
          fillColor: STATUS_TINT[cell.tone], margin: [8, 6, 8, 6], fontSize: 9.5,
        };
      }
      return { text: cell.text, color: BODY, margin: [8, 6, 8, 6], fontSize: 9.5 };
    }),
  );
  return {
    table: { headerRows: 1, widths: tableWidths(block.headers.length), body: [header, ...body] },
    layout: {
      hLineWidth: (i: number, node: any) => (i === 0 || i === 1 || i === node.table.body.length ? 0 : 0.6),
      vLineWidth: () => 0,
      hLineColor: () => LINE,
      fillColor: (rowIndex: number, node: any) => {
        if (rowIndex === 0) return null;
        const hasTone = node.table.body[rowIndex].some((c: any) => c.fillColor);
        return !hasTone && rowIndex % 2 === 0 ? CARD : null;
      },
    },
    margin: [0, 2, 0, 12],
  };
}

function renderBlock(block: ReportBlock, accent: string): any[] {
  switch (block.kind) {
    case 'paragraph':
      return [{ text: block.text, style: 'p', margin: [0, 0, 0, 6] }];
    case 'bullets':
      // Clone — pdfmake mutates `ul` items in place during layout (would corrupt shared source data).
      return [{ ul: [...block.items], style: 'p', margin: [4, 1, 0, 9], markerColor: accent }];
    case 'callout':
      return [{
        table: {
          widths: [3.5, '*'],
          body: [[
            { text: '', fillColor: block.tone ? toneColor(block.tone) : BRAND },
            { text: block.text, fillColor: block.tone ? STATUS_TINT[block.tone] : BLUE_TINT, color: NAVY, bold: true, margin: [12, 8, 12, 8], lineHeight: 1.2 },
          ]],
        },
        layout: 'noBorders',
        margin: [0, 2, 0, 10],
      }];
    case 'steps':
      return block.items.map((item, i) => ({
        margin: [0, 2, 0, 8],
        stack: [
          {
            columns: [
              { width: 20, svg: badgeSvg(i + 1, accent) },
              { width: '*', text: item.title, bold: true, color: NAVY, fontSize: 11, margin: [0, 2, 0, 0] },
            ],
            columnGap: 6,
          },
          ...(item.sub && item.sub.length
            ? [{ ul: [...item.sub], margin: [24, 4, 0, 0], style: 'p', markerColor: accent }]
            : []),
        ],
      }));
    case 'table':
      return [renderTable(block)];
    case 'path': {
      const s = stepperSvg(block.steps, block.currentIndex, accent);
      return [{ svg: s.svg, width: s.width, margin: [4, 6, 0, 12] }];
    }
    default:
      return [];
  }
}

function normalizeBlocks(blocks: ReportBlock[]): ReportBlock[] {
  const out: ReportBlock[] = [];
  for (let i = 0; i < blocks.length; i++) {
    const b = blocks[i];
    if (!b) continue;
    if (b.kind === 'paragraph' && /^Recommended Path:/i.test(b.text)) {
      const next = blocks[i + 1];
      if (next && next.kind === 'callout' && b.text.includes(next.text)) i++;
      out.push({ kind: 'callout', text: b.text, tone: 'neutral' });
      continue;
    }
    out.push(b);
  }
  return out;
}

function sectionHeader(index: number, title: string): any {
  return {
    stack: [
      {
        columns: [
          { width: 20, svg: badgeSvg(index, BRAND) },
          { width: '*', text: title, style: 'h2', margin: [0, 2, 0, 0] },
        ],
        columnGap: 8,
      },
      { canvas: [{ type: 'line', x1: 0, y1: 0, x2: CONTENT_WIDTH, y2: 0, lineWidth: 1, lineColor: LINE }], margin: [0, 6, 0, 9] },
    ],
  };
}

function renderSection(section: ReportSection, index: number): any[] {
  const blocks = normalizeBlocks(section.blocks);
  const isDecision = /Decision$/.test(section.title);
  const body: any[] = [];
  for (const b of blocks) {
    if (isDecision && b.kind === 'table' && b.headers.length === 2) body.push(...statGrid(b.rows));
    else body.push(...renderBlock(b, BRAND));
  }
  // Keep each section together: if it doesn't fit in the remaining space pdfmake moves
  // the whole section to a new page instead of splitting it (e.g. header on one page,
  // table on the next). pdfmake still breaks a section that is taller than a full page,
  // so this never drops content.
  return [{ stack: [sectionHeader(index, section.title), ...body], unbreakable: true, margin: [0, 14, 0, 0] }];
}

// ── Document assembly ─────────────────────────────────────────────────────────

export function buildDocDefinition(report: CapitalReport, opts: ReportPdfOptions): any {
  const cover: any[] = [
    { image: LOGO_WHITE_DATAURL, width: 134, margin: [0, 2, 0, 0] },
    { text: `${report.domain.toUpperCase()} REPORT`, font: 'MontserratSemi', color: '#BFD9F5', characterSpacing: 2.5, fontSize: 9, margin: [0, 34, 0, 0] },
    { text: report.stage, color: '#FFFFFF', bold: true, fontSize: 26, lineHeight: 1.08, margin: [0, 10, 110, 0] },
    { text: `Profile · ${report.profile}`, color: '#DCEAFB', fontSize: 11, bold: true, margin: [0, 10, 0, 0] },
    { text: report.intro, color: '#C6D7EC', fontSize: 10, lineHeight: 1.4, margin: [0, 8, 80, 0] },
    { svg: scoreCardSvg(opts.score), width: 230, alignment: 'center', margin: [0, 26, 0, 0] },
    {
      text: [
        { text: 'Prepared for ', color: '#9FB6D4' },
        { text: opts.userName, color: '#FFFFFF', bold: true },
        { text: `   ·   ${opts.dateLabel}`, color: '#9FB6D4' },
      ],
      absolutePosition: { x: 40, y: 800 }, fontSize: 9.5,
    },
    { text: '', pageBreak: 'after' },
  ];

  const sectionContent = report.sections.flatMap((section, i) => {
    if (section.title === 'Final CTA') {
      const cta = section.blocks.find((b) => b.kind === 'callout') as { text: string } | undefined;
      const banner = ctaBannerSvg(cta?.text ?? '');
      return [{ svg: banner.svg, width: CONTENT_WIDTH, margin: [0, 24, 0, 0] }];
    }
    const number = report.sections.slice(0, i).filter((s) => s.title !== 'Final CTA').length + 1;
    return renderSection(section, number);
  });

  return {
    pageSize: 'A4',
    pageMargins: [40, 44, 40, 52],
    info: { title: `${report.domain} Report — ${report.key}`, author: 'Conncct', subject: report.stage },
    background: (currentPage: number) =>
      currentPage === 1
        ? { svg: coverBackgroundSvg(), width: PAGE_W, height: PAGE_H }
        : { canvas: [{ type: 'rect', x: 0, y: 0, w: PAGE_W, h: 5, color: BRAND }] },
    footer: (currentPage: number, pageCount: number) => {
      if (currentPage === 1) return undefined;
      return {
        margin: [40, 8, 40, 0],
        stack: [
          { canvas: [{ type: 'line', x1: 0, y1: 0, x2: CONTENT_WIDTH, y2: 0, lineWidth: 0.8, lineColor: LINE }], margin: [0, 0, 0, 6] },
          {
            columns: [
              { text: 'CONNCCT  ·  CONFIDENTIAL', font: 'MontserratSemi', color: FAINT, fontSize: 7.5, characterSpacing: 0.6 },
              { text: `${report.domain} · ${report.key}`, color: FAINT, fontSize: 7.5, alignment: 'center' },
              { text: `${currentPage} / ${pageCount}`, color: FAINT, fontSize: 7.5, alignment: 'right' },
            ],
          },
        ],
      };
    },
    content: [...cover, ...sectionContent],
    styles: {
      h2: { fontSize: 13.5, bold: true, color: NAVY },
      p: { fontSize: 10, color: BODY, lineHeight: 1.32 },
    },
    defaultStyle: { font: 'Montserrat', fontSize: 10, color: BODY, lineHeight: 1.32 },
  };
}

// ── Public API ─────────────────────────────────────────────────────────────────

export async function downloadCapitalReportPdf(report: CapitalReport, opts: ReportPdfOptions): Promise<void> {
  const pdfMakeMod: any = await import('pdfmake/build/pdfmake');
  const vfsMod: any = await import('pdfmake/build/vfs_fonts');
  const { MONTSERRAT_VFS } = await import('@/utils/montserratFont');
  const pdfMake = pdfMakeMod.default ?? pdfMakeMod;
  const robotoVfs = vfsMod.default ?? vfsMod;

  pdfMake.addVirtualFileSystem({ ...robotoVfs, ...MONTSERRAT_VFS });
  pdfMake.setFonts({
    Montserrat: {
      normal: 'Montserrat-Regular.ttf', bold: 'Montserrat-Bold.ttf',
      italics: 'Montserrat-Regular.ttf', bolditalics: 'Montserrat-Bold.ttf',
    },
    MontserratSemi: {
      normal: 'Montserrat-SemiBold.ttf', bold: 'Montserrat-Bold.ttf',
      italics: 'Montserrat-SemiBold.ttf', bolditalics: 'Montserrat-Bold.ttf',
    },
    Roboto: {
      normal: 'Roboto-Regular.ttf', bold: 'Roboto-Medium.ttf',
      italics: 'Roboto-Italic.ttf', bolditalics: 'Roboto-MediumItalic.ttf',
    },
  });

  const fileName = `${report.domain.replace(/\s+/g, '-')}-Report-${report.key}.pdf`;
  pdfMake.createPdf(buildDocDefinition(report, opts)).download(fileName);
}

// Open the capital report in a NEW TAB for online viewing (with the browser's
// built-in download control). Pass a window opened synchronously on the user's
// click to dodge popup blockers; falls back to opening one here, then to download.
export async function openCapitalReportPdf(report: CapitalReport, opts: ReportPdfOptions, targetWindow?: Window | null): Promise<void> {
  const pdfMakeMod: any = await import('pdfmake/build/pdfmake');
  const vfsMod: any = await import('pdfmake/build/vfs_fonts');
  const { MONTSERRAT_VFS } = await import('@/utils/montserratFont');
  const pdfMake = pdfMakeMod.default ?? pdfMakeMod;
  const robotoVfs = vfsMod.default ?? vfsMod;

  pdfMake.addVirtualFileSystem({ ...robotoVfs, ...MONTSERRAT_VFS });
  pdfMake.setFonts({
    Montserrat: { normal: 'Montserrat-Regular.ttf', bold: 'Montserrat-Bold.ttf', italics: 'Montserrat-Regular.ttf', bolditalics: 'Montserrat-Bold.ttf' },
    MontserratSemi: { normal: 'Montserrat-SemiBold.ttf', bold: 'Montserrat-Bold.ttf', italics: 'Montserrat-SemiBold.ttf', bolditalics: 'Montserrat-Bold.ttf' },
    Roboto: { normal: 'Roboto-Regular.ttf', bold: 'Roboto-Medium.ttf', italics: 'Roboto-Italic.ttf', bolditalics: 'Roboto-MediumItalic.ttf' },
  });

  const fileName = `${report.domain.replace(/\s+/g, '-')}-Report-${report.key}.pdf`;
  // pdfmake 0.3.x: getBlob() returns a Promise<Blob> (no callback).
  const blob: Blob = await pdfMake.createPdf(buildDocDefinition(report, opts)).getBlob();
  const url = URL.createObjectURL(blob);
  const w = targetWindow && !targetWindow.closed ? targetWindow : window.open('', '_blank');
  if (w) {
    try { w.location.href = url; } catch { /* ignore */ }
  } else {
    const a = document.createElement('a');
    a.href = url; a.download = fileName;
    document.body.appendChild(a); a.click(); a.remove();
  }
  setTimeout(() => URL.revokeObjectURL(url), 120000);
}
